import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function AccountSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userData, setUserData] = useState(null);
  const [hospitalData, setHospitalData] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/hospital/setup/verify/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setUserData(response.data.user);
        setHospitalData(response.data.hospital);
      }
    } catch (error) {
      setTokenError(error.response?.data?.detail || 'Invalid or expired setup link');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/hospital/setup/complete', {
        token,
        password,
        confirm_password: confirmPassword
      });
      
      toast.success('Account setup completed successfully!');
      
      // Store the token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.hospital) {
        localStorage.setItem('hospital', JSON.stringify(response.data.hospital));
      }
      
      // Redirect to hospital dashboard
      setTimeout(() => {
        if (response.data.hospital?.login_slug) {
          navigate(`/login/${response.data.hospital.login_slug}`);
        } else {
          navigate('/');
        }
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete setup');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strengthColor = () => {
    const s = passwordStrength();
    if (s <= 2) return 'bg-red-500';
    if (s <= 3) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const strengthText = () => {
    const s = passwordStrength();
    if (s <= 2) return 'Weak';
    if (s <= 3) return 'Medium';
    return 'Strong';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#4A238F] to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#4A238F] to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Invalid Setup Link</h2>
              <p className="text-white/70 mb-6">{tokenError}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-white text-[#4A238F] hover:bg-white/90"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#4A238F] to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Hospital Branding */}
        {hospitalData && (
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ backgroundColor: hospitalData.branding?.primary_color || '#4A238F' }}>
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">{hospitalData.name}</h1>
            <p className="text-white/60">Account Setup</p>
          </div>
        )}

        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-2">
              <Shield className="h-6 w-6 text-emerald-400" />
            </div>
            <CardTitle className="text-white">Welcome, {userData?.full_name}!</CardTitle>
            <p className="text-white/60 text-sm">
              Create a secure password to complete your account setup
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email display (read-only) */}
              <div className="space-y-2">
                <Label className="text-white/80">Email</Label>
                <Input
                  type="email"
                  value={userData?.email || ''}
                  disabled
                  className="bg-white/5 border-white/20 text-white/60"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="text-white/80">Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/5 border-white/20 text-white pr-10"
                    placeholder="Create a strong password"
                    required
                    data-testid="setup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {password && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded ${i < passwordStrength() ? strengthColor() : 'bg-white/20'}`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs ${strengthColor().replace('bg-', 'text-')}`}>
                      {strengthText()}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label className="text-white/80">Confirm Password *</Label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-white/5 border-white/20 text-white pr-10"
                    placeholder="Confirm your password"
                    required
                    data-testid="setup-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-400">Passwords do not match</p>
                )}
                {confirmPassword && password === confirmPassword && password.length >= 8 && (
                  <p className="text-xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Passwords match
                  </p>
                )}
              </div>

              {/* Password requirements */}
              <div className="p-3 bg-white/5 rounded-lg space-y-1">
                <p className="text-xs text-white/60 font-medium">Password must contain:</p>
                <ul className="text-xs text-white/50 space-y-0.5">
                  <li className={password.length >= 8 ? 'text-emerald-400' : ''}>
                    {password.length >= 8 ? '✓' : '○'} At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) ? 'text-emerald-400' : ''}>
                    {/[A-Z]/.test(password) ? '✓' : '○'} One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(password) ? 'text-emerald-400' : ''}>
                    {/[a-z]/.test(password) ? '✓' : '○'} One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(password) ? 'text-emerald-400' : ''}>
                    {/[0-9]/.test(password) ? '✓' : '○'} One number
                  </li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-3"
                data-testid="complete-setup-btn"
              >
                {submitting ? 'Setting up your account...' : 'Complete Setup & Login'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-xs text-white/40 text-center">
                By completing setup, you agree to securely manage your hospital's patient data
                in compliance with healthcare regulations.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
